// Product photos come off a phone camera, often several MB. They are shrunk in the browser before upload so they are quick on a mobile
// connection; the server then re-encodes them again (a full size and a thumbnail), so this only has to make them small enough to send.

export const MAX_PRODUCT_PHOTOS = 5;
const MAX_SIDE = 1600;
const QUALITY = 0.85;
export const MIN_SIDE = 400; // the shorter side a photo must have (the server enforces the same)
export const GOOD_SIDE = 800; // from here a photo looks good; between the two it only gets a warning
const SQUARE_LIMIT = 1.6; // wider or taller than this and the shop's square pictures crop its edges

/** What is wrong with a photo of this size: an Error message when it must be refused, and warnings when it may be used. */
export function checkPhotoSize(width, height, name, { minSide = MIN_SIDE, goodSide = GOOD_SIDE } = {}) {
  const shorter = Math.min(width, height);
  const longer = Math.max(width, height);
  if (shorter < minSide) {
    return { error: `${name} is too small (${width} x ${height} pixels). Use a photo that is at least ${minSide} pixels on its shorter side; ${goodSide} or more looks best.`, warnings: [] };
  }
  const warnings = [];
  if (shorter < goodSide) warnings.push(`${name} is on the small side (${width} x ${height}) and may look soft on large screens. ${goodSide} pixels or more looks best.`);
  if (longer / shorter > SQUARE_LIMIT) warnings.push(`${name} is not square: the shop shows square pictures, so its edges may be cropped.`);
  return { error: null, warnings };
}

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
  img.src = url;
});

/** Turns a photo the user picked into a JPEG ready to upload, with any warnings about it. Throws an Error with a message for the user when it can't be used. */
export async function prepareProductPhoto(file, limits) {
  if (!/^image\//.test(file.type)) throw new Error(`${file.name} is not a photo`);
  let img;
  try {
    img = await loadImage(file);
  } catch (e) {
    // e.g. an iPhone HEIC photo on a browser that can't open it
    throw new Error(`${file.name} can't be opened here. Choose a JPG or PNG.`);
  }
  const check = checkPhotoSize(img.naturalWidth, img.naturalHeight, file.name, limits);
  if (check.error) throw new Error(check.error);
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // a transparent PNG would otherwise turn black in a JPEG
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (!blob) throw new Error(`${file.name} could not be prepared`);
  const stem = file.name.replace(/\.[^.]+$/, '') || 'photo';
  return { file: new File([blob], `${stem}.jpg`, { type: 'image/jpeg' }), warnings: check.warnings };
}
